import React from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import ButtonV2 from "../../../../../common/components/atom/ButtonV2/Button";
import TagInput from "../../../../../common/components/molecule/TagInput/TagInput";
import { CalendarIllustration } from "../icons";
import { ExtractAggregation } from "../../../types/extract.types";
import styles from "./ConfigurationSectionView.module.scss";

export interface ConfigurationSectionViewProps {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  maxDate: Dayjs;
  minEndDate: Dayjs;
  maxEndDate: Dayjs;
  aggregation: ExtractAggregation;
  onAggregationChange: (a: ExtractAggregation) => void;
  tags: string[];
  onTagsChange: (next: string[]) => void;
  tagSuggestions?: string[];
  canStart: boolean;
  isSubmitting: boolean;
  hasActiveJob: boolean;
  onStart: () => void;
}

export default function ConfigurationSectionView({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  maxDate,
  minEndDate,
  maxEndDate,
  aggregation,
  onAggregationChange,
  tags,
  onTagsChange,
  tagSuggestions,
  canStart,
  isSubmitting,
  hasActiveJob,
  onStart,
}: ConfigurationSectionViewProps) {
  return (
    <div className={styles["configuration"]}>
      <div className={styles["configuration__header"]}>
        <span className={styles["configuration__badge"]}>3</span>
        <h2 className={styles["configuration__title"]}>Configuration finale</h2>
      </div>

      <div className={styles["configuration__illustration"]}>
        <CalendarIllustration />
      </div>

      <div className={styles["configuration__dates"]}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date début"
            value={startDate}
            onChange={onStartDateChange}
            maxDate={maxDate}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          <DatePicker
            label="Date fin"
            value={endDate}
            onChange={onEndDateChange}
            minDate={minEndDate}
            maxDate={maxEndDate}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        </LocalizationProvider>
        <span className={styles["configuration__date-warning"]}>
          Maximum 31 jours
        </span>
      </div>

      <fieldset className={styles["configuration__field"]}>
        <legend className={styles["configuration__field-legend"]}>
          Mode des données
        </legend>
        <label className={styles["configuration__radio-row"]}>
          <input
            type="radio"
            name="extract-aggregation"
            value="hourly"
            checked={aggregation === "hourly"}
            onChange={() => onAggregationChange("hourly")}
          />
          Moyennées par heure
          <span className={styles["configuration__radio-default"]}>(par défaut)</span>
        </label>
        <label className={styles["configuration__radio-row"]}>
          <input
            type="radio"
            name="extract-aggregation"
            value="raw"
            checked={aggregation === "raw"}
            onChange={() => onAggregationChange("raw")}
          />
          Réelles brutes
          <span className={styles["configuration__radio-warn"]}>
            ⚠ volume plus important
          </span>
        </label>
      </fieldset>

      <fieldset className={styles["configuration__field"]}>
        <legend className={styles["configuration__field-legend"]}>
          Tags compteurs (optionnel)
        </legend>
        <TagInput
          value={tags}
          onChange={onTagsChange}
          placeholder="Ajouter un tag…"
          suggestions={tagSuggestions}
        />
        <span className={styles["configuration__field-help"]}>
          Vide = tous les compteurs du compte. Sinon, seuls les compteurs portant
          tous les tags sélectionnés seront extraits.
        </span>
      </fieldset>

      <div className={styles["configuration__actions"]}>
        <ButtonV2
          onClick={onStart}
          variant="contained"
          color="primary"
          size="medium"
          disabled={!canStart || isSubmitting}
          fullWidth
        >
          {isSubmitting ? "Lancement…" : "Démarrer l'extraction"}
        </ButtonV2>

        {hasActiveJob && (
          <span className={styles["configuration__active-hint"]}>
            Une extraction est déjà en cours pour ce compte. Vous pouvez la suivre
            dans l'onglet « Mes extractions ».
          </span>
        )}
      </div>
    </div>
  );
}
